import { useEffect, useState } from "react"
import FormErrors from "components/formParts/formErrors"
import Callout from "components/callout/callout"
import { useTranslation } from "react-i18next"
import { CategoryWrite, InputMaxLength } from "types"
import { filterInputName } from "utils/filter"
import TextInput from "components/inputs/textInput"
import TextAreaInput from "components/inputs/textareaInput"
import { logger } from "utils/secureLogger"
import useCategories from "hooks/useCategories"
import useLayout from "hooks/useLayout"
import Form from "components/forms/form"
import CategorySelector from "components/inputs/categorySelector"
import Button from "components/inputs/button"
import Section from "components/layout/section"
import { ReactComponent as SaveIcon } from "assets/icons/save.svg"
import ProcessingButton from "components/inputs/processingButton"
import { decodeFirebaseError } from "utils/firebaseHelpers"

interface EditCategoryFormProps {
    storageId: string
    categoryId: string
}

const EditCategoryForm: React.FC<EditCategoryFormProps> = ({ storageId, categoryId }) => {

    const { t } = useTranslation()

    // Redux hooks.
    // const dispatch = useDispatch<AppDispatch>()

    // Layout hooks.
    const { addToast } = useLayout()

    // Component state.
    const [id, setId] = useState<number>(1)
    const [name, setName] = useState(``)
    const [description, setDescription] = useState(``)
    const [storageRequirements, setStorageRequirements] = useState(``)
    const [notes, setNotes] = useState(``)

    // Form status.
    const [errors, setErrors] = useState<string[]>([]);

    const {
        category,
        categories,
        allCategories,
        categoriesStatus,
        reloadCategories,
        updateCategoryAsync,
        isUpdating
    } = useCategories({ storageId, categoryId })
        
    const [parentCategory, setParentCategory] = useState<string>(`_`)

    // Set state.
    useEffect(() => {
        if (!category) return
        setId(category.categoryId)
        setName(category.name)
        setDescription(category.description)
        setStorageRequirements(category.storageRequirements)
        setNotes(category.notes)
        setParentCategory(category.relations?.parentCategory || `_`)
    }, [category])

    // Handle submit.
    const handleSubmit = async (event: React.FormEvent) => {
        logger.debug('Edit category form submitted')
        setErrors([])
        event.preventDefault()
        if (storageId === undefined) return
        
        try {
            const data: CategoryWrite = {
                ids: {
                    storageId: storageId,
                    categoryId: categoryId,
                },
                _version: 1,
                categoryId: id,
                name: name,
                description: description,
                storageRequirements: storageRequirements,
                notes: notes,
                relations: {
                    parentCategory: parentCategory === `_` ? undefined : parentCategory
                },
                isDeleted: false,
            }
            await updateCategoryAsync(data)
            logger.info('Category updated successfully', { categoryId }, 'EditCategory')
            addToast({ text: t('toastCategoryUpdated'), colorScheme: 'green' })
        } catch (error: unknown) {
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error))
            setErrors([errorMessage])
            addToast({ text: t('toastCategoryError'), colorScheme: 'red' })
            logger.error('Failed to update category', error)
        }
    }
    
    // Handle id change.
    const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const numericValue = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 1
        setId(numericValue)
    }

    // Handle name change.
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value)
    }

    // Handle description change.
    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(e.target.value)
    }

    // Handle storage requirements change.
    const handleStorageRequirementsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setStorageRequirements(e.target.value)
    }

    const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(e.target.value)
    }

    // Handle errors.
    if (categoriesStatus === `loading`) return <Callout type={`neutral`} loadingIcon={true}>{t('loadingStorage')}</Callout>
    if (!category && categoriesStatus === `error`) return <Callout type={`error`}>{t('failedToLoadStorage')}</Callout>

    // Return the JSX.
    return (
        <Form
            className={`group`}
            onSubmit={handleSubmit}
            isLoading={isUpdating}
        >
            <Section>
                <TextInput id={`id`} label={t('id')} required={true} value={id.toString()} onChange={handleIdChange} maxLength={InputMaxLength.short} />
                <TextInput id={`name`} label={t('labelCategoryName')} required={true} value={name} onChange={handleNameChange} maxLength={InputMaxLength.short} />
                <TextAreaInput label={t('description')} value={description} onChange={handleDescriptionChange} maxLength={InputMaxLength.long} />
                <TextInput label={t('storageRequirements')} value={storageRequirements} onChange={handleStorageRequirementsChange} maxLength={InputMaxLength.long} />
                <TextAreaInput label={t('notes')} value={notes} onChange={handleNotesChange} maxLength={InputMaxLength.extraLong} />
                <div className={`input-holder-wide`}>
                    <label>{t('labelParentCategory')}</label>
                    <CategorySelector
                        categories={allCategories.filter((cat) => cat.id !== categoryId)}
                        allCategories={allCategories}
                        selectedCategory={parentCategory}
                        setSelectedCategory={setParentCategory}
                        hiddenCategories={[categoryId]}
                        allowCreate={false}
                    />
                </div>
            </Section>
            <Section>
                <div className={`input-holder`}>
                    {isUpdating ? (
                        <ProcessingButton design="primary" />
                    ) : (
                        <Button wideOnMobile type="submit" design="primary"><SaveIcon /><span>{t('saveChanges')}</span></Button>
                    )}
                </div>
                {errors.length > 0 && <FormErrors errors={errors} />}
            </Section>
        </Form>
    )

}

export default EditCategoryForm



